// pages/W_HR_R360.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, { Column, Paging, FilterRow, HeaderFilter, SearchPanel, Sorting, Export } from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R360({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const code = user.username.trim().toUpperCase();
      const found = custList.find(item => item.CODE_CD.trim().toUpperCase() === code);
      setSelectedCust(found?.CODE_CD || null);
      if (!found) setError(`사용자 코드(${user.username})가 CUST 목록에 없습니다.`);
    }
  }, [custList, user]);

  useEffect(() => {
    setSelectedDevice(null);
    setDeviceList([]);
    if (!selectedCust) return;
    (async () => {
      setDeviceLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/oracle/device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST first.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || 'ALL',
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo)
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R360`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);

      if (res.data.data && res.data.data.length > 0 && typeof res.data.data[0] === 'object' && !Array.isArray(res.data.data[0])) {
        setData(res.data.data.map((row, idx) => ({
          ...row,
          id: `${row.LOT_NO || 'NA'}-${row.WFR_RUN_NO || 'NA'}-${idx}`
        })));
      } else {
        setData((res.data.data || []).map((row, idx) => ({
          id: `${row[2] || 'NA'}-${row[3] || 'NA'}-${idx}`,
          CUST_CD: row[0],
          DEVICE: row[1],
          LOT_NO: row[2],
          WFR_RUN_NO: row[3],
          LOT_QTY: row[4],
          DATECODE: row[5],
          IN_DATE: row[6]
        })));
      }
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = e => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("INPUT LOT LIST");
    exportDataGrid({ component: e.component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "INPUT_LOT_LIST.xlsx");
      });
    });
    e.cancel = true;
  };

  return (
    // [수정] 페이지 전체를 Flexbox 레이아웃으로 설정
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3>INPUT LOT LIST</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox
            dataSource={custList}
            value={selectedCust}
            valueExpr="CODE_CD"
            displayExpr="CODE_CD"
            onValueChanged={e => setSelectedCust(e.value)}
            disabled={comboLoading || user?.role === "CUST"}
            searchEnabled
            showClearButton={user?.role !== "CUST"}
            placeholder="Select CUST"
            label="CUST"
          />
          <SelectBox
            dataSource={deviceList}
            value={selectedDevice}
            valueExpr="CODE_CD"
            displayExpr="CODE_CD"
            onValueChanged={e => setSelectedDevice(e.value)}
            disabled={deviceLoading}
            searchEnabled
            showClearButton
            placeholder="DEVICE (All)"
            label="DEVICE"
          />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "red", fontWeight: 'bold' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}>
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%" // [수정] 부모의 높이를 100% 사용
          showBorders
          columnAutoWidth
          allowColumnResizing
          hoverStateEnabled
        >
          <Export enabled />
          <Paging defaultPageSize={10} />
          <FilterRow visible />
          <HeaderFilter visible />
          <SearchPanel visible highlightCaseSensitive={false} />
          <Sorting mode="multiple" />
          <Column dataField="CUST_CD" caption="CUST" />
          <Column dataField="DEVICE" caption="DEVICE" />
          <Column dataField="LOT_NO" caption="LOT NO" />
          <Column dataField="WFR_RUN_NO" caption="WFR RUN NO" />
          <Column dataField="LOT_QTY" caption="LOT QTY" />
          <Column dataField="DATECODE" caption="DATE CODE" />
          <Column dataField="IN_DATE" caption="IN DATE" />
        </DataGrid>
      </div>
    </div>
  );
}